// ============================================
// Audit Logs Hooks - TanStack Query
// ============================================

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { AuditLogListParams, AuditAction, UUID } from '@conai/shared-types';
import { useAuditLogsService } from '../providers/ApiProvider';
import { queryKeys } from '../config/query-client';
import { api } from '../config/api';

// ============================================
// Query Hooks
// ============================================

/**
 * Hook to list audit logs with pagination and filters
 */
export function useAuditLogs(params: AuditLogListParams = {}) {
  const auditLogsService = useAuditLogsService();

  return useQuery({
    queryKey: queryKeys.auditLogs.list(params as Record<string, unknown>),
    queryFn: () => auditLogsService.list(params),
    // #103: giữ data cũ trong lúc refetch khi đổi page/search. Thiếu cái này thì
    // logsData = undefined giữa chừng -> totalPages sập về 1 -> Pagination bị
    // unmount ngay lúc đang bấm -> mất active indicator và nút Back "chết".
    placeholderData: keepPreviousData,
  });
}

/**
 * Hook to get an audit log by ID
 */
export function useAuditLog(id: string, options?: { enabled?: boolean }) {
  const auditLogsService = useAuditLogsService();

  return useQuery({
    queryKey: queryKeys.auditLogs.detail(id),
    queryFn: () => auditLogsService.getById(id),
    enabled: options?.enabled !== false && !!id,
  });
}

/**
 * Hook to get the full list of audit entity/resource types (GET /audit/entity-types/)
 */
export function useAuditEntityTypes() {
  return useQuery({
    queryKey: [...queryKeys.auditLogs.all, 'entity-types'],
    queryFn: () => api.client.get<{ entity_types?: string[] }>('/audit/entity-types/'),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to get audit log statistics
 */
export function useAuditLogStats() {
  const auditLogsService = useAuditLogsService();

  return useQuery({
    queryKey: queryKeys.auditLogs.stats(),
    queryFn: () => auditLogsService.getStats(),
  });
}

/**
 * Hook to get logs by action type
 */
export function useAuditLogsByAction(action: AuditAction, params?: Omit<AuditLogListParams, 'action'>) {
  const auditLogsService = useAuditLogsService();

  return useQuery({
    queryKey: queryKeys.auditLogs.list({ ...params, action }),
    queryFn: () => auditLogsService.getByAction(action, params),
  });
}

/**
 * Hook to get logs by entity type
 */
export function useAuditLogsByEntityType(
  resourceType: string,
  params?: Omit<AuditLogListParams, 'resource_type'>
) {
  const auditLogsService = useAuditLogsService();

  return useQuery({
    queryKey: queryKeys.auditLogs.list({ ...params, resource_type: resourceType }),
    queryFn: () => auditLogsService.getByEntityType(resourceType, params),
  });
}

/**
 * Hook to get logs by user
 */
export function useAuditLogsByUser(userId: UUID, params?: Omit<AuditLogListParams, 'actor_user_id'>) {
  const auditLogsService = useAuditLogsService();

  return useQuery({
    queryKey: queryKeys.auditLogs.list({ ...params, actor_user_id: userId }),
    queryFn: () => auditLogsService.getByUser(userId, params),
    enabled: !!userId,
  });
}

/**
 * Hook to get logs for a specific entity
 */
export function useAuditLogsForEntity(
  resourceType: string,
  resourceId: UUID,
  params?: Omit<AuditLogListParams, 'resource_type' | 'resource_id'>
) {
  const auditLogsService = useAuditLogsService();

  return useQuery({
    queryKey: queryKeys.auditLogs.list({
      ...params,
      resource_type: resourceType,
      resource_id: resourceId,
    }),
    queryFn: () => auditLogsService.getByEntity(resourceType, resourceId, params),
    enabled: !!resourceType && !!resourceId,
  });
}

/**
 * Hook to get today's audit logs
 */
export function useTodayAuditLogs(params?: Omit<AuditLogListParams, 'start_date'>) {
  const auditLogsService = useAuditLogsService();

  return useQuery({
    queryKey: [...queryKeys.auditLogs.lists(), 'today', params],
    queryFn: () => auditLogsService.getToday(params),
  });
}

/**
 * Hook to get recent activity
 */
export function useRecentActivity(limit: number = 10) {
  const auditLogsService = useAuditLogsService();

  return useQuery({
    queryKey: [...queryKeys.auditLogs.lists(), 'recent', limit],
    queryFn: () => auditLogsService.getRecentActivity(limit),
  });
}

/**
 * Hook to get login/logout history for a user
 */
export function useAuthHistory(userId: UUID) {
  const auditLogsService = useAuditLogsService();

  return useQuery({
    queryKey: [...queryKeys.auditLogs.lists(), 'auth-history', userId],
    queryFn: () => auditLogsService.getAuthHistory(userId),
    enabled: !!userId,
  });
}
